import { z } from 'zod';

/**
 * The option, card and feedback shapes the activity setups are built from.
 *
 * Every object here is a `looseObject`. Real story JSON carries product fields this
 * package does not model — `ActivitySetup` in hydra says so in as many words: the
 * fields "must survive a load → edit → save round-trip byte-identical". A strict
 * schema would reject a document Phoenix publishes happily, so unknown keys ride
 * through and only the fields an activity cannot work without are required.
 */

/** The two halves of a graded activity's response copy. */
export const FeedbackMessage = z.looseObject({
  title: z.string().optional(),
  message: z.string().optional(),
});

export const Feedback = z.looseObject({
  correct: FeedbackMessage.optional(),
  incorrect: FeedbackMessage.optional(),
});

/**
 * One selectable option. The studio authors two shapes — `label`/`value` for the
 * quiz family, `text` for dialogue — and the runtime reads whichever is present,
 * so neither can be required on its own. `optionText` below is the check that one
 * of them actually is.
 */
export const Option = z.looseObject({
  id: z.string().optional(),
  value: z.string().optional(),
  label: z.string().optional(),
  text: z.string().optional(),
  emoji: z.string().optional(),
  imageUrl: z.string().optional(),
  outcomeId: z.string().optional(),
});

export type Option = z.infer<typeof Option>;

/** An option's visible text, across both authored shapes. Mirrors hydra's `optionLabel`. */
export const optionText = (option: Option): string =>
  (option.text ?? option.label ?? option.value ?? '').trim();

/** The value a correct answer is written against. Mirrors hydra's `optionValue`. */
export const optionValue = (option: Option): string =>
  (option.value ?? option.id ?? '').trim();

/**
 * Fields shared by every graded question: the ask, the response copy, the grading rules.
 * `question` admits null because a gameshow leaves it so (its host asks the question in
 * dialogue); whether a given template allows that is `validateActivity`'s call, made
 * against the template it is told about — see `templatePolicy.ts`.
 */
export const questionFields = {
  question: z.string().nullable().optional(),
  feedback: Feedback.optional(),
  revealAnswer: z.boolean().optional(),
  requireCorrectAnswer: z.boolean().optional(),
  shuffle: z.boolean().optional(),
};

/**
 * The authoring fields hydra writes onto a draft's setup. They are the brief, not
 * configuration, and they survive production — so every setup schema admits them
 * rather than each producer having to remember to strip them.
 */
export const draftFields = {
  prompt: z.string().optional(),
  promptStale: z.boolean().optional(),
  outcomeNotes: z.record(z.string(), z.string()).optional(),
};
